import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { GET_FABRIC_REVIEWS } from 'mocks/queries';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import browserStorage from 'components/consumer/browser-storage';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';

import getRandomId from 'commons/getRandomId';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import withApolloClient from 'components/hoc/withApolloClient';
import withScreen from 'components/hoc/withScreen';

export const withState = WrappedComponent => {
  class GridStateHOC extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        allDataList: [],
        productsList: [],
        productIdsList: [],
        photosList: [],
        reviewsList: [],
        allFabricReviews: [],
        queries: [],
        currentPage: 0,
        selectedGridItem: null,
        selectedGridItemCustomProps: {},
        currentSelection: 'all',
        hoveredValue: {
          isHovered: false,
          hoveredOver: null,
        },
        modalValue: {
          isClicked: false,
          modalFor: null,
        },
        loadingData: false,
        loadingMore: false,
        hasLoadMore: true,
        hasLoadMoreProducts: true,
        hasLoadMoreReviews: false,
      };
    }

    componentDidMount() {
      if (!this.props.isMobile) {
        let selectedItem = null;
        if (this.props.location.pathname.includes('collections/')) {
          selectedItem = browserStorage().getItem('selectedCollectionItem');
          browserStorage().removeItem('selectedCollectionItem');
        } else if (this.props.location.pathname.includes('customer-photos/')) {
          selectedItem = browserStorage().getItem('selectedCustomerPhotoItem');
          browserStorage().removeItem('selectedCustomerPhotoItem');
        } else if (this.props.location.pathname.includes('swatches/')) {
          selectedItem = browserStorage().getItem('selectedMaterialSwatchItem');
          setTimeout(
            () => browserStorage().removeItem('selectedMaterialSwatchItem'),
            500
          );
        }
        if (selectedItem) {
          const selectedGridItem = JSON.parse(selectedItem);
          this.updateSelectedGridItem(selectedGridItem);
        }
      }

      if (this.props?.initialSelection) {
        this.updateSelectedGridItem(this.props?.initialSelection, true);
      }
    }

    componentDidUpdate(prevProps) {
      if (
        prevProps.initialSelection !== this.props.initialSelection &&
        this.props.initialSelection
      ) {
        this.updateSelectedGridItem(this.props.initialSelection);
      }
    }

    getFabricReviews = async (fabricId, callback) => {
      const data = await this.props.client.query({
        query: GET_FABRIC_REVIEWS,
        variables: { fabricValueId: fabricId?.toString() },
        fetchPolicy: 'network-only',
      });

      let reviewsFromRedis = get(data, 'data.getFabricReviews.reviews', []);
      reviewsFromRedis = reviewsFromRedis.map(review => ({
        card_type: 'review',
        id: getRandomId('review-id'),
        ...review,
      }));

      this.setState({ allFabricReviews: reviewsFromRedis }, () => {
        callback();
      });
    };

    updateSelectedGridItem = (selectedGridItem, skipCallback = false) => {
      this.setState(
        {
          currentPage: 0,
          allDataList: [],
          productsList: [],
          productIdsList: [],
          photosList: [],
          reviewsList: [],
          allFabricReviews: [],

          selectedGridItem,
          selectedGridItemCustomProps: selectedGridItem
            ? this.props.getCustomProps(
                selectedGridItem,
                this.props.location.pathname
              )
            : {},
        },
        () => {
          if (selectedGridItem) {
            if (
              selectedGridItem.option?.type === 'Fabric' ||
              selectedGridItem.option?.type === 'Leather'
            ) {
              this.getFabricReviews(
                selectedGridItem.option.id,
                this.queryAlgolia
              );
            } else {
              this.queryAlgolia();
            }

            // callback
            if (this.props?.onSelectionUpdate && !skipCallback) {
              this.props.onSelectionUpdate({ ...selectedGridItem });
            }
          }
        }
      );
    };

    updateResultSelection = resultType => {
      this.setState({
        currentSelection: resultType,
      });
    };

    mergeList = (productsList, photosList, reviewsList) => {
      const list = [];
      let i = 0;
      let j = 0;
      let k = 0;
      if (this.props.gridItemType === 'fabric') {
        while (productsList[i] || photosList[j]) {
          if (productsList[i]) {
            list.push(productsList[i]);
            i += 1;
          }
          if (photosList[j]) {
            list.push(photosList[j]);
            j += 1;
          }
        }
        list.push(...reviewsList);
        return list;
      }
      // render every third card as customer-photo
      while (productsList[j] || photosList[k]) {
        i += 1;
        if (i % 3 === 0) {
          if (photosList[k]) {
            list.push(photosList[k]);
            k += 1;
          }
        } else if (productsList[j]) {
          list.push(productsList[j]);
          j += 1;
        }
      }

      return list;
    };

    queryAlgolia = async (page = 0) => {
      const selectedGridItemType = this.props.gridItemType;
      const selectedGridItemName = this.state.selectedGridItemCustomProps
        .selectedItemName;

      if (!selectedGridItemType || !selectedGridItemName) {
        return;
      }

      const loadingFlag = page === 0 ? 'loadingData' : 'loadingMore';
      this.setState({
        [loadingFlag]: true,
      });

      if (selectedGridItemType === 'customer-photo') {
        const { selectedItemProducts } = this.state.selectedGridItemCustomProps;
        const productIds = selectedItemProducts
          ? selectedItemProducts.map(p => parseInt(p.product_id, 10))
          : [];

        this.setState({
          productIdsList: productIds,
          [loadingFlag]: false,
          hasLoadMore: false,
        });
        return;
      }

      let productIds = [];
      let photoCardsList = [];
      let reviewsList = [];

      // Query Algolia for product IDs and photos in that fabric
      const updatedAlgoliaQuery = [
        {
          indexName: ALGOLIA_INDEX_NAME.PRODUCTS,
          query: '',
          params: {
            hitsPerPage: 9,
            filters: '',
            numericFilters: [
              'item_availability=0',
              'visibility>0',
              'visibility<4',
            ],
          },
        },
        {
          indexName: 'customer_photos',
          query: '',
          params: {
            hitsPerPage: 6,
            filters: '',
          },
        },
      ];

      updatedAlgoliaQuery[0].params.page = page;
      updatedAlgoliaQuery[1].params.page = page;

      if (selectedGridItemType === 'fabric') {
        updatedAlgoliaQuery[0].params.filters = `option_value:"${selectedGridItemName}"`;
        updatedAlgoliaQuery[1].params.filters = `products.upholstery:"${selectedGridItemName}"`;
        updatedAlgoliaQuery[0].params.hitsPerPage = 6;
      } else if (selectedGridItemType === 'collection') {
        const productFamily = selectedGridItemName
          ? selectedGridItemName.replace('Collection', '').trim()
          : '';

        updatedAlgoliaQuery[0].query = productFamily;
        updatedAlgoliaQuery[0].params.restrictSearchableAttributes = ['family'];
        updatedAlgoliaQuery[1].query = productFamily;
        updatedAlgoliaQuery[1].params.filters = `products.family:"${productFamily}"`;

        // for collections that dont belong to collection-named product family
        // usually their names are two-worded
        if (productFamily.split(' ').length > 1) {
          updatedAlgoliaQuery[0].params.restrictSearchableAttributes = ['name'];
          updatedAlgoliaQuery[1].params.filters = `products.value:"${productFamily}"`;
        }
      }

      this.setState(
        {
          queries: updatedAlgoliaQuery,
        },
        () => {
          searchClient.search(this.state.queries).then(async response => {
            const { results } = response;

            const productResults = results[0].hits.length
              ? results[0].hits
              : [];
            productIds = productResults.map(hit =>
              parseInt(hit.product_id, 10)
            );

            photoCardsList = results[1].hits.length ? results[1].hits : [];
            photoCardsList = photoCardsList.map(photo => ({
              _sort: parseInt(photo.photo_id, 10) % 1000,
              card_type: 'customer_photo',
              ...photo,
              image_link: transformInspirationImageUrl(photo.image_link),
            }));
            if (selectedGridItemType === 'fabric') {
              reviewsList = this.state.allFabricReviews
                ? this.state.allFabricReviews.slice(page * 6, (page + 1) * 6)
                : [];
            }

            const productIdsDataList = productIds.map(id => ({
              id,
              _sort: id % 1000,
              card_type: 'product',
            }));

            const allDataList =
              selectedGridItemType === 'fabric'
                ? this.mergeList(
                    productIdsDataList,
                    photoCardsList,
                    reviewsList
                  )
                : this.mergeList(productIdsDataList, photoCardsList);

            this.setState(prevState => ({
              allDataList: [...prevState.allDataList, ...allDataList],
              productIdsList: [...prevState.productIdsList, ...productIds],
              photosList: [...prevState.photosList, ...photoCardsList],
              reviewsList: [...prevState.reviewsList, ...reviewsList],
              [loadingFlag]: false,
              hasLoadMoreProducts: productIds.length >= 9,
              hasLoadMoreReviews:
                prevState.reviewsList.length + reviewsList.length <
                prevState.allFabricReviews?.length,
            }));
          });
        }
      );
    };

    onModalMoveToNextPhoto = () =>
      this.setState(prevState => {
        const updatedModal = { ...prevState.modalValue };
        updatedModal.photoIndex =
          updatedModal.photoIndex <= prevState.photosList.length
            ? updatedModal.photoIndex + 1
            : -1;
        updatedModal.modalFor =
          prevState.photosList[updatedModal.photoIndex] || null;
        return { modalValue: updatedModal };
      });

    onModalMoveToPreviousPhoto = () =>
      this.setState(prevState => {
        const updatedModal = { ...prevState.modalValue };
        updatedModal.photoIndex =
          updatedModal.photoIndex >= 1 ? updatedModal.photoIndex - 1 : -1;
        updatedModal.modalFor =
          prevState.photosList[updatedModal.photoIndex] || null;
        return { modalValue: updatedModal };
      });

    toggleModalView = photo =>
      this.setState(prevState => {
        const updatedModal = { ...prevState.modalValue };
        updatedModal.isClicked = !prevState.modalValue.isClicked;
        updatedModal.modalFor = updatedModal.isClicked ? photo : null;
        updatedModal.photoIndex = photo
          ? prevState.photosList.indexOf(photo)
          : -1;
        return { modalValue: updatedModal };
      });

    toggleReviewOnHover = photoId =>
      this.setState(prevState => {
        const updatedHoveredValue = { ...prevState.hoveredValue };
        if (photoId === 'out') {
          updatedHoveredValue.isHovered = false;
          updatedHoveredValue.hoveredOver = null;
        } else {
          updatedHoveredValue.isHovered = !prevState.hoveredValue.isHovered;
          updatedHoveredValue.hoveredOver = photoId;
        }
        return { hoveredValue: updatedHoveredValue };
      });

    loadMore = () => {
      const { currentPage } = this.state;
      const page = (currentPage || 0) + 1;
      this.setState({ currentPage: page }, () => {
        this.queryAlgolia(page);
      });
    };

    render() {
      const actions = {
        updateSelectedGridItem: this.updateSelectedGridItem,
        updateResultSelection: this.updateResultSelection,
        onModalMoveToNextPhoto: this.onModalMoveToNextPhoto,
        onModalMoveToPreviousPhoto: this.onModalMoveToPreviousPhoto,
        toggleModalView: this.toggleModalView,
        toggleReviewOnHover: this.toggleReviewOnHover,
        loadMore: this.state.hasLoadMore ? this.loadMore : null,
        loadMoreProducts: this.state.hasLoadMoreProducts ? this.loadMore : null,
      };
      const state = {
        allDataList: this.state.allDataList,
        productsList: this.state.productsList,
        productIdsList: this.state.productIdsList,
        photosList: this.state.photosList,
        reviewsList: this.state.reviewsList,
        currentSelection: this.state.currentSelection,
        hoveredValue: this.state.hoveredValue,
        modalValue: this.state.modalValue,
        loadingData: this.state.loadingData,
        loadingMore: this.state.loadingMore,
        selectedGridItem: this.state.selectedGridItem,
        selectedGridItemCustomProps: this.state.selectedGridItemCustomProps,
        hasLoadMoreReviews: this.state.hasLoadMoreReviews,
      };
      return (
        <WrappedComponent state={state} actions={actions} {...this.props} />
      );
    }
  }

  GridStateHOC.propTypes = {
    client: PropTypes.shape({
      query: PropTypes.func,
    }),
    getCustomProps: PropTypes.func,
    gridItemType: PropTypes.string,
    location: PropTypes.shape({
      includes: PropTypes.string,
      pathname: PropTypes.string,
    }),
    initialSelection: PropTypes.objectOf(PropTypes.object),
    isMobile: PropTypes.bool,
    onSelectionUpdate: PropTypes.func,
  };

  return withScreen(withApolloClient(withRouter(GridStateHOC)));
};
